import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useCreateStore, useUpdateStore } from "@/api/mutations";
import type { StoreDetail, StoreInput } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { formErrorsFrom } from "@/lib/form-errors";
import { US_STATES, US_TIME_ZONES } from "@/lib/us-locations";

const STATE_OPTIONS = Object.entries(US_STATES).sort(([, a], [, b]) => a.localeCompare(b));
const ZONE_OPTIONS = Object.entries(US_TIME_ZONES);

const optional = z
  .string()
  .trim()
  .transform((v) => v || null);
const schema = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(120),
  address: z.string().trim().min(3, "Enter the street address").max(200),
  city: z.string().trim().min(2, "Enter the city").max(100),
  state: z.string().min(1, "Pick a state"),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, "Enter a 5-digit ZIP code"),
  timezone: z.string().min(1, "Pick a time zone"),
  extra: optional.pipe(z.string().max(300, "Keep it under 300 characters").nullable()),
  email: optional.pipe(z.email("Enter a valid email").nullable()),
  contactPhone: optional,
  phoneNumber: optional,
  infobipNumber: optional,
  telnyxNumber: optional,
});

type FieldName = keyof z.input<typeof schema>;
type Values = Record<FieldName, string>;

const EMPTY: Values = {
  name: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  timezone: "America/Chicago",
  extra: "",
  email: "",
  contactPhone: "",
  phoneNumber: "",
  infobipNumber: "",
  telnyxNumber: "",
};

const fromStore = (s: StoreDetail): Values => ({
  name: s.name,
  address: s.address ?? "",
  city: s.city ?? "",
  state: s.state ?? "",
  postalCode: s.postalCode ?? "",
  timezone: s.timezone,
  extra: s.extra ?? "",
  email: s.email ?? "",
  contactPhone: s.contactPhone ?? "",
  phoneNumber: s.phoneNumber ?? "",
  infobipNumber: s.infobipNumber ?? "",
  telnyxNumber: s.telnyxNumber ?? "",
});

/** Create a store in an organization, or edit an existing one (same form). */
export function StoreFormDialog({
  open,
  onOpenChange,
  organization,
  store,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  organization: { id: string; name: string };
  store?: StoreDetail;
  onSaved?: (store: StoreDetail) => void;
}) {
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<FieldName | "form", string>>>({});
  const create = useCreateStore(organization.id);
  const update = useUpdateStore(store?.id ?? "");
  const pending = create.isPending || update.isPending;
  useEffect(() => {
    if (open) {
      setValues(store ? fromStore(store) : EMPTY);
      setErrors({});
    }
  }, [open, store]);

  const set = (k: FieldName) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fe = z.flattenError(parsed.error).fieldErrors as Partial<Record<FieldName, string[]>>;
      return setErrors(Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, v?.[0]])));
    }
    setErrors({});
    const input: StoreInput = parsed.data;
    const handlers = {
      onSuccess: (saved: StoreDetail) => {
        toast.success(store ? "Store updated" : `${saved.name} created`);
        onOpenChange(false);
        onSaved?.(saved);
      },
      onError: (err: unknown) => {
        const { fields, form } = formErrorsFrom(err);
        setErrors({ ...fields, form: form ?? undefined });
      },
    };
    if (store) update.mutate(input, handlers);
    else create.mutate(input, handlers);
  };

  const field = (name: FieldName, label: string, opts: { type?: string; placeholder?: string; hint?: string; className?: string } = {}) => (
    <Field data-invalid={Boolean(errors[name])} className={`gap-1 ${opts.className ?? ""}`}>
      <FieldLabel htmlFor={`store-${name}`}>{label}</FieldLabel>
      <Input
        id={`store-${name}`}
        type={opts.type ?? "text"}
        placeholder={opts.placeholder}
        value={values[name]}
        onChange={set(name)}
        aria-invalid={Boolean(errors[name])}
        autoComplete="off"
      />
      {opts.hint && !errors[name] ? <FieldDescription className="text-xs">{opts.hint}</FieldDescription> : null}
      <FieldError className="text-xs">{errors[name]}</FieldError>
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>{store ? "Edit store" : "New store"}</DialogTitle>
            <DialogDescription>{store ? store.id : `A new rooftop in ${organization.name}.`}</DialogDescription>
          </DialogHeader>
          <div className="my-4 max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            {errors.form ? (
              <Alert variant="destructive">
                <AlertDescription>{errors.form}</AlertDescription>
              </Alert>
            ) : null}
            <FieldGroup className="gap-3">
              {field("name", "Store name", { placeholder: "EchoPark Long Beach" })}
              <Field data-invalid={Boolean(errors.extra)} className="gap-1">
                <FieldLabel htmlFor="store-extra">Extra details (optional)</FieldLabel>
                <Textarea
                  id="store-extra"
                  rows={2}
                  placeholder="located inside Long Beach BMW"
                  value={values.extra}
                  onChange={set("extra")}
                  aria-invalid={Boolean(errors.extra)}
                />
                {!errors.extra ? (
                  <FieldDescription className="text-xs">Anything that helps people find or identify the store, kept separate from its name.</FieldDescription>
                ) : null}
                <FieldError className="text-xs">{errors.extra}</FieldError>
              </Field>
            </FieldGroup>
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">Address</legend>
              {field("address", "Street address", { placeholder: "1660 Ximeno Ave" })}
              <div className="grid gap-3 sm:grid-cols-[1fr_11rem_7rem]">
                {field("city", "City", { placeholder: "Long Beach" })}
                <Field data-invalid={Boolean(errors.state)} className="gap-1">
                  <FieldLabel htmlFor="store-state">State</FieldLabel>
                  <NativeSelect id="store-state" value={values.state} onChange={set("state")} className="w-full" aria-invalid={Boolean(errors.state)}>
                    <NativeSelectOption value="">Choose…</NativeSelectOption>
                    {/* An older store may hold a value outside the list: keep it selectable. */}
                    {values.state && !(values.state in US_STATES) ? <NativeSelectOption value={values.state}>{values.state}</NativeSelectOption> : null}
                    {STATE_OPTIONS.map(([code, name]) => (
                      <NativeSelectOption key={code} value={code}>
                        {name} ({code})
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <FieldError className="text-xs">{errors.state}</FieldError>
                </Field>
                {field("postalCode", "ZIP code", { placeholder: "90804" })}
              </div>
              <Field data-invalid={Boolean(errors.timezone)} className="gap-1">
                <FieldLabel htmlFor="store-timezone">Time zone</FieldLabel>
                <NativeSelect id="store-timezone" value={values.timezone} onChange={set("timezone")} className="w-full">
                  {values.timezone && !(values.timezone in US_TIME_ZONES) ? (
                    <NativeSelectOption value={values.timezone}>{values.timezone.replaceAll("_", " ")} (current)</NativeSelectOption>
                  ) : null}
                  {ZONE_OPTIONS.map(([zone, label]) => (
                    <NativeSelectOption key={zone} value={zone}>
                      {label} — {zone.replaceAll("_", " ")}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError className="text-xs">{errors.timezone}</FieldError>
              </Field>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">Contact</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {field("email", "Email (optional)", { type: "email", placeholder: "store@dealer.com" })}
                {field("contactPhone", "Store phone (optional)", { type: "tel", placeholder: "(562) 555-0100" })}
              </div>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">SMS numbers</legend>
              <p className="text-muted-foreground text-xs">
                One per provider the store texts sellers from. Each number is unique across stores; leave a provider empty until its number is provisioned. A
                lead texts from one of them, and replies always come from the number the seller texted.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {field("phoneNumber", "Twilio number", { type: "tel", placeholder: "+15625550199" })}
                {field("infobipNumber", "Infobip number", { type: "tel", placeholder: "+15625550198" })}
                {field("telnyxNumber", "Telnyx number", { type: "tel", placeholder: "+15625550197" })}
              </div>
            </fieldset>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <Spinner data-icon="inline-start" /> : null}
              {store ? "Save changes" : "Create store"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
